/**
 * Tressette — server-authoritative rules engine (Italian trick-taking game, 4 players in two partnerships).
 *
 * Rules implemented (toggles live in {@link TressetteConfig}):
 *  - 4 players in two partnerships: seats 0 & 2 (team 0) against seats 1 & 3 (team 1).
 *  - 40-card deck: the standard deck without the 8, 9 and 10. Ranking, high to low: 3, 2, A, K, Q, J, 7, 6, 5, 4.
 *    There is no trump suit.
 *  - Each player is dealt ten cards (no drawing). The player left of the dealer leads and every player must follow
 *    the led suit when able; the highest card of the led suit wins the trick, and the winner leads the next one.
 *  - Card points are counted in thirds: Ace = 3 (one point), and each 3, 2, K, Q, J = 1 (a third of a point). The last
 *    trick is worth one point (3 thirds). That is 32 thirds in the cards plus 3 for the last trick, about 11⅔ points.
 *  - At the end of a hand each partnership scores its thirds divided by three, rounded down (the fraction is lost).
 *  - The match ends when a partnership reaches `targetScore` points (a tie above the target is played on).
 *    `maxHands` is a hard cap so a match always terminates (then the team with more points wins, equal totals share
 *    first). Partners share a place.
 */
import { buildDeck, parseCard, removeCards, sortHand, type CardId } from '../../core/cards.js';
import { checkInt, mergeKnown } from '../../core/config.js';
import { shuffle, type Rng } from '../../core/rng.js';
import {
  assertEngine,
  EngineError,
  isPlainObject,
  type EngineEvent,
  type GameEngine,
  type Standing,
} from '../../core/types.js';

export interface TressetteConfig {
  /** Match ends when a partnership reaches this many points. */
  targetScore: number;
  /** Hard cap on hands so a match always terminates. */
  maxHands: number;
  handEndDelayMs: number;
  trickEndDelayMs: number;
}

export type TressettePhase = 'PLAYING' | 'TRICK_END' | 'HAND_END' | 'MATCH_END';

export interface TressetteTrickPlay {
  seat: number;
  card: CardId;
}

export interface TressetteCompletedTrick {
  leader: number;
  plays: TressetteTrickPlay[];
  winner: number;
  /** Card thirds in the trick (the last-trick bonus is not included). */
  thirds: number;
}

/** Per-hand result for one partnership (public once the hand ends). */
export interface TressetteTeamHand {
  /** Thirds collected, including the last-trick bonus. */
  thirds: number;
  /** Whole points scored: thirds / 3 rounded down. */
  points: number;
}

export interface TressetteState {
  config: TressetteConfig;
  players: string[];
  hand: number;
  dealer: number;
  phase: TressettePhase;
  hands: CardId[][];
  /** Thirds collected by each team this hand (the last-trick bonus is added when the hand ends). */
  teamThirds: number[];
  /** Tricks won by each seat this hand. */
  tricksWon: number[];
  turn: number;
  trick: TressetteTrickPlay[];
  tricks: TressetteCompletedTrick[];
  /** Points scored by each team across hands, index = team (seat % 2). */
  teamScores: number[];
  /** One row per finished hand: [team0, team1]. */
  handResults: TressetteTeamHand[][];
}

export type TressetteAction = { type: 'play'; card: CardId };

export type TressetteLegal = { kind: 'play'; cards: CardId[] } | { kind: 'none' };

export interface TressetteView {
  gameType: 'TRESSETTE';
  config: TressetteConfig;
  seat: number | null;
  phase: TressettePhase;
  round: number;
  dealer: number;
  turn: number;
  hand: CardId[];
  handCounts: number[];
  /** Thirds collected by each team this hand. */
  teamThirds: number[];
  tricksWon: number[];
  trick: TressetteTrickPlay[];
  lastTrick: TressetteCompletedTrick | null;
  tricks: TressetteCompletedTrick[];
  /** Points scored by each team across hands. */
  teamScores: number[];
  handResults: TressetteTeamHand[][];
  /** Points scored by each seat's team (so generic score UIs work). */
  totals: number[];
}

const DEFAULTS: TressetteConfig = {
  targetScore: 21,
  maxHands: 30,
  handEndDelayMs: 5000,
  trickEndDelayMs: 900,
};

const SEATS = 4;
const TRICKS = 10;
/** The last trick is worth one point. */
const LAST_TRICK_THIRDS = 3;

export const tressetteTeamOf = (seat: number): number => seat % 2;
export const tressettePartnerOf = (seat: number): number => (seat + 2) % SEATS;

/** Card value in thirds of a point: Ace 3, and 3, 2, K, Q, J one third each. */
export function tressetteThirds(card: CardId): number {
  switch (parseCard(card).rank) {
    case 14:
      return 3;
    case 3:
    case 2:
    case 13:
    case 12:
    case 11:
      return 1;
    default:
      return 0;
  }
}

/** Trick-taking strength within a suit: 3 > 2 > A > K > Q > J > 7 > 6 > 5 > 4. */
export function tressetteStrength(card: CardId): number {
  switch (parseCard(card).rank) {
    case 3:
      return 10;
    case 2:
      return 9;
    case 14:
      return 8;
    case 13:
      return 7;
    case 12:
      return 6;
    case 11:
      return 5;
    default:
      // 7 → 4, 6 → 3, 5 → 2, 4 → 1
      return parseCard(card).rank - 3;
  }
}

/** The 40-card Tressette deck. */
export const tressetteDeck = (): CardId[] => buildDeck().filter((c) => { const r = parseCard(c).rank; return r !== 8 && r !== 9 && r !== 10; });

/** The seat that wins a trick: the highest card of the led suit. */
export function tressetteTrickWinner(plays: readonly TressetteTrickPlay[]): number {
  const lead = parseCard(plays[0]!.card).suit;
  let best = plays[0]!;
  for (const p of plays.slice(1)) {
    if (parseCard(p.card).suit === lead && tressetteStrength(p.card) > tressetteStrength(best.card)) best = p;
  }
  return best.seat;
}

/** Cards a player may play: the led suit when held, otherwise anything. */
export function tressetteLegalCards(hand: readonly CardId[], trick: readonly TressetteTrickPlay[]): CardId[] {
  if (trick.length === 0) return [...hand];
  const led = parseCard(trick[0]!.card).suit;
  const follow = hand.filter((c) => parseCard(c).suit === led);
  return follow.length > 0 ? follow : [...hand];
}

function deal(state: TressetteState, rng: Rng, events: EngineEvent[]): void {
  const deck = shuffle(tressetteDeck(), rng);
  const hands: CardId[][] = [[], [], [], []];
  for (let i = 0; i < deck.length; i++) hands[(state.dealer + 1 + i) % SEATS]!.push(deck[i]!);
  state.hands = hands.map((h) => sortHand(h));
  state.teamThirds = [0, 0];
  state.tricksWon = [0, 0, 0, 0];
  state.trick = [];
  state.tricks = [];
  state.phase = 'PLAYING';
  state.turn = (state.dealer + 1) % SEATS;
  events.push({ type: 'dealt', hand: state.hand, dealer: state.dealer, counts: state.hands.map((h) => h.length) });
}

/** Partners share a place: the team with more points is 1st, the other 2nd (both 1st on a tie). */
function teamStandings(state: TressetteState): Standing[] {
  const [a, b] = state.teamScores as [number, number];
  return [0, 1, 2, 3].map((seat) => {
    const team = tressetteTeamOf(seat);
    const mine = team === 0 ? a : b;
    const theirs = team === 0 ? b : a;
    return { seat, score: mine, place: mine >= theirs ? 1 : 2 };
  });
}

export const tressetteEngine: GameEngine<TressetteConfig, TressetteState, TressetteAction, TressetteView, TressetteLegal> = {
  gameType: 'TRESSETTE',
  kind: 'match',

  defaultConfig: () => ({ ...DEFAULTS }),

  normalizeConfig(input) {
    const c = mergeKnown(DEFAULTS, input);
    checkInt('targetScore', c.targetScore, 1, 100);
    checkInt('maxHands', c.maxHands, 1, 60);
    checkInt('handEndDelayMs', c.handEndDelayMs, 0, 60000);
    checkInt('trickEndDelayMs', c.trickEndDelayMs, 0, 10000);
    return c;
  },

  seatLimits: () => ({ min: SEATS, max: SEATS }),

  createState(config, seats, rng) {
    assertEngine(seats.length === SEATS, 'INVALID_CONFIG', 'Tressette needs exactly 4 seats');
    const players = Array<string>(SEATS).fill('');
    for (const s of seats) {
      assertEngine(s.seat >= 0 && s.seat < SEATS && !players[s.seat], 'SEAT_TAKEN', `Bad seat ${s.seat}`);
      players[s.seat] = s.playerId;
    }
    const state: TressetteState = {
      config,
      players,
      hand: 1,
      dealer: rng.int(SEATS),
      phase: 'PLAYING',
      hands: [],
      teamThirds: [0, 0],
      tricksWon: [],
      turn: 0,
      trick: [],
      tricks: [],
      teamScores: [0, 0],
      handResults: [],
    };
    deal(state, rng, []);
    return state;
  },

  parseAction(raw) {
    if (!isPlainObject(raw)) throw new EngineError('INVALID_ACTION', 'Action must be an object');
    if (raw.type === 'play' && typeof raw.card === 'string' && raw.card.length <= 4) return { type: 'play', card: raw.card };
    throw new EngineError('INVALID_ACTION', 'Unknown Tressette action');
  },

  applyAction(state, seat, action) {
    assertEngine(state.phase !== 'MATCH_END', 'GAME_OVER', 'Match is over');
    assertEngine(seat === state.turn && state.phase === 'PLAYING', 'NOT_YOUR_TURN', 'Not your turn');
    const events: EngineEvent[] = [];
    const hand = state.hands[seat]!;
    assertEngine(hand.includes(action.card), 'INVALID_CARD', 'Card not in hand');
    assertEngine(tressetteLegalCards(hand, state.trick).includes(action.card), 'ILLEGAL_MOVE', 'You must follow the led suit');
    removeCards(hand, [action.card]);
    state.trick.push({ seat, card: action.card });
    events.push({ type: 'cardPlayed', seat, card: action.card });
    if (state.trick.length < SEATS) {
      state.turn = (seat + 1) % SEATS;
      return events;
    }
    const winner = tressetteTrickWinner(state.trick);
    const thirds = state.trick.reduce((n, p) => n + tressetteThirds(p.card), 0);
    state.tricksWon[winner]!++;
    state.teamThirds[tressetteTeamOf(winner)]! += thirds;
    state.tricks.push({ leader: state.trick[0]!.seat, plays: state.trick, winner, thirds });
    events.push({ type: 'trickWon', seat: winner, trickNo: state.tricks.length, thirds });
    state.phase = 'TRICK_END';
    state.turn = winner;
    return events;
  },

  getActors(state) {
    return state.phase === 'PLAYING' ? [state.turn] : [];
  },

  getLegalActions(state, seat) {
    if (seat !== state.turn || state.phase !== 'PLAYING') return { kind: 'none' };
    return { kind: 'play', cards: tressetteLegalCards(state.hands[seat]!, state.trick) };
  },

  getTimeoutAction(state, seat) {
    // The cheapest legal card: no points first, lowest strength first.
    const cost = (c: CardId) => tressetteThirds(c) * 20 + tressetteStrength(c);
    const lowest = [...tressetteLegalCards(state.hands[seat]!, state.trick)].sort((a, b) => cost(a) - cost(b))[0]!;
    return { type: 'play', card: lowest };
  },

  getView(state, seat) {
    return {
      gameType: 'TRESSETTE',
      config: state.config,
      seat,
      phase: state.phase,
      round: state.hand,
      dealer: state.dealer,
      turn: state.turn,
      hand: seat === null ? [] : [...state.hands[seat]!],
      handCounts: state.hands.map((h) => h.length),
      teamThirds: [...state.teamThirds],
      tricksWon: [...state.tricksWon],
      trick: state.phase === 'TRICK_END' ? [...(state.tricks.at(-1)?.plays ?? [])] : [...state.trick],
      lastTrick: state.tricks.at(-1) ?? null,
      tricks: state.tricks.map((t) => ({ ...t, plays: [...t.plays] })),
      teamScores: [...state.teamScores],
      handResults: state.handResults.map((r) => r.map((x) => ({ ...x }))),
      totals: [0, 1, 2, 3].map((s) => state.teamScores[tressetteTeamOf(s)]!),
    };
  },

  getSystemStep(state) {
    if (state.phase === 'TRICK_END') return { kind: 'collectTrick', delayMs: state.config.trickEndDelayMs };
    if (state.phase === 'HAND_END') return { kind: 'nextHand', delayMs: state.config.handEndDelayMs };
    return null;
  },

  runSystemStep(state, rng) {
    const events: EngineEvent[] = [];
    if (state.phase === 'TRICK_END') {
      state.trick = [];
      if (state.tricks.length < TRICKS) {
        state.phase = 'PLAYING';
        events.push({ type: 'trickCollected', leader: state.turn });
        return events;
      }
      // The last trick is worth one point to the team that won it (state.turn is its winner).
      state.teamThirds[tressetteTeamOf(state.turn)]! += LAST_TRICK_THIRDS;
      const cfg = state.config;
      const results: TressetteTeamHand[] = [0, 1].map((team) => ({ thirds: state.teamThirds[team]!, points: Math.floor(state.teamThirds[team]! / 3) }));
      results.forEach((r, team) => {
        state.teamScores[team]! += r.points;
      });
      state.handResults.push(results);
      events.push({ type: 'handEnd', hand: state.hand, results, teamScores: [...state.teamScores], teamThirds: [...state.teamThirds] });
      const [a, b] = state.teamScores as [number, number];
      const reached = (a >= cfg.targetScore || b >= cfg.targetScore) && a !== b;
      const capped = state.hand >= cfg.maxHands;
      if (reached || capped) {
        state.phase = 'MATCH_END';
        events.push({ type: 'matchEnd', standings: teamStandings(state) });
      } else {
        state.phase = 'HAND_END';
      }
      return events;
    }
    if (state.phase === 'HAND_END') {
      state.hand++;
      state.dealer = (state.dealer + 1) % SEATS;
      deal(state, rng, events);
      return events;
    }
    return events;
  },

  isFinished: (state) => state.phase === 'MATCH_END',

  getStandings(state): Standing[] {
    return teamStandings(state);
  },

  getRoundsCompleted: (state) => state.handResults.length,
};
